/**
 * Ensures URL has a protocol prefix. If the URL doesn't start with http:// or https://,
 * prepends https:// to make it valid for URL parsing.
 * This handles cases where environment variables have their protocol stripped
 */
function ensureProtocol(url: string | undefined): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
}

const VERCEL_URL = process.env.NEXT_PUBLIC_VERCEL_URL ? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}` : "";
const RAILWAY_STATIC_URL = process.env.RAILWAY_STATIC_URL ? `https://${process.env.RAILWAY_STATIC_URL}` : "";
const HEROKU_URL = process.env.HEROKU_APP_NAME ? `https://${process.env.HEROKU_APP_NAME}.herokuapp.com` : "";
const RENDER_URL = process.env.RENDER_EXTERNAL_URL ? `https://${process.env.RENDER_EXTERNAL_URL}` : "";
export const CALCOM_ENV = process.env.CALCOM_ENV || process.env.NODE_ENV;
export const IS_PRODUCTION = CALCOM_ENV === "production";
export const IS_PRODUCTION_BUILD = process.env.NODE_ENV === "production";
export const ORGANIZER_EMAIL_EXEMPT_DOMAINS = process.env.ORGANIZER_EMAIL_EXEMPT_DOMAINS || "";
const IS_DEV = CALCOM_ENV === "development";
export const SINGLE_ORG_SLUG = process.env.NEXT_PUBLIC_SINGLE_ORG_SLUG;
/** https://app.cal.com */
export const WEBAPP_URL =
  ensureProtocol(process.env.NEXT_PUBLIC_WEBAPP_URL) ||
  VERCEL_URL ||
  RAILWAY_STATIC_URL ||
  HEROKU_URL ||
  RENDER_URL ||
  "http://localhost:3000";

// OAuth needs to have HTTPS(which is not generally setup locally) and a valid tld(*.local isn't a valid tld)
// So for development purpose, we would stick to localhost only
export const WEBAPP_URL_FOR_OAUTH = IS_PRODUCTION || IS_DEV ? WEBAPP_URL : "http://localhost:3000";

/** @deprecated use `WEBAPP_URL` */
export const BASE_URL = WEBAPP_URL;
export const WEBSITE_URL = ensureProtocol(process.env.NEXT_PUBLIC_WEBSITE_URL) || "https://kalienda.be";

/**
 * URL du **site vitrine** (pages marketing, légales, centre d'aide, journal des versions).
 *
 * ⚠️ Ce n'est PAS `WEBSITE_URL`, malgré le nom de cette dernière. En production,
 * `NEXT_PUBLIC_WEBSITE_URL` n'est passée ni par la CI (`.github/workflows/deploy.yml`) ni par
 * `docker-compose.prod.yml`, donc `apps/web/next.config.ts` la rabat sur `NEXT_PUBLIC_WEBAPP_URL` :
 * `WEBSITE_URL` vaut alors `https://rdv.kalienda.be`, l'**application**. Tout lien public bâti
 * dessus (CGU, confidentialité, aide…) tombait donc sur un 404.
 *
 * Et on ne peut pas corriger cela en câblant `NEXT_PUBLIC_WEBSITE_URL` vers la vitrine : la même
 * variable alimente `CAL_URL` juste en dessous, qui construit les liens de réservation publics
 * `${CAL_URL}/username/event`. Les envoyer vers un site statique casserait toutes les réservations.
 *
 * En dur volontairement : la vitrine est un site unique, sans équivalent par environnement, et une
 * `NEXT_PUBLIC_*` de plus serait une variable de build à câbler dans trois fichiers pour aucun gain.
 *
 * Les chemins de la vitrine s'écrivent avec un **slash final** : le site est généré au format
 * répertoire (`/cgu/index.html`), et l'omettre coûte une redirection 301 à chaque clic.
 */
export const MARKETING_SITE_URL = "https://kalienda.be";
export const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || "Kalienda";
export const SUPPORT_MAIL_ADDRESS = process.env.NEXT_PUBLIC_SUPPORT_MAIL_ADDRESS || "contact@kalienda.be";
export const COMPANY_NAME = process.env.NEXT_PUBLIC_COMPANY_NAME || "Kalienda";
export const SENDER_ID = process.env.NEXT_PUBLIC_SENDER_ID || "Kalienda";
export const SENDER_NAME = process.env.NEXT_PUBLIC_SENDGRID_SENDER_NAME || "Kalienda";
export const EMAIL_FROM_NAME = process.env.EMAIL_FROM_NAME || APP_NAME;

/**
 * Pays supposé quand un patient saisit un numéro de téléphone au format **national**, sans indicatif.
 * Le marché est belge : « 0470 12 34 56 » doit se lire `+32470123456`.
 *
 * ⚠️ **Ce n'est pas un détail de confort, c'est un garde-fou.** Avec la valeur précédente (`"FR"`,
 * héritée de rdv-pro.be), ce même numéro se lisait `+33470123456` — un **fixe français parfaitement
 * valide**. La normalisation « réussissait » donc, l'opt-in SMS était enregistré, et le message partait
 * vers un inconnu en France. Ce sont les **mobiles** — la quasi-totalité des cas réels — qui échouaient
 * ainsi en s'ouvrant ; les fixes, eux, échouaient proprement.
 *
 * ⚠️ **Ne jamais rétablir un repli « essayer BE puis FR »** : « 02 512 34 567 » (un fixe belge avec un
 * chiffre de trop, la faute de frappe la plus banale) est invalide en BE mais valide en FR — le repli
 * transformerait une coquille en vraie ligne française et rouvrirait exactement cette fuite. Un patient
 * dont le numéro n'est pas belge dispose déjà d'une issue sans ambiguïté : la forme internationale
 * (`+33…`), que `defaultRegion` n'affecte pas.
 */
export const DEFAULT_PHONE_REGION = "BE";

// This is the URL from which all Cal Links and their assets are served.
// Use website URL to make links shorter(cal.com and not app.cal.com)
// As website isn't setup for preview environments, use the webapp url instead
// If it's a .vercel.app domain, keep it.
// Else use the website url if defined and finally fallback to the webapp url
export const CAL_URL = new URL(WEBAPP_URL).hostname.endsWith(".vercel.app")
  ? WEBAPP_URL
  : ensureProtocol(process.env.NEXT_PUBLIC_WEBSITE_URL) || WEBAPP_URL;

export const IS_CALCOM =
  WEBAPP_URL &&
  (new URL(WEBAPP_URL).hostname.endsWith("cal.com") ||
    new URL(WEBAPP_URL).hostname.endsWith("cal.dev") ||
    new URL(WEBAPP_URL).hostname.endsWith("cal.qa") ||
    new URL(WEBAPP_URL).hostname.endsWith("cal-staging.com") ||
    new URL(WEBAPP_URL).hostname.endsWith("cal.eu"));

export const CONSOLE_URL =
  new URL(WEBAPP_URL).hostname.endsWith(".cal.dev") ||
  new URL(WEBAPP_URL).hostname.endsWith(".cal.qa") ||
  new URL(WEBAPP_URL).hostname.endsWith(".cal-staging.com") ||
  process.env.NODE_ENV !== "production"
    ? `https://console.cal.dev`
    : `https://console.cal.com`;
const CAL_DOMAINS = [".cal.com", ".cal.dev", ".cal.eu", ".cal.qa"];
const WEBAPP_HOSTNAME = new URL(WEBAPP_URL).hostname;
export const IS_SELF_HOSTED = !CAL_DOMAINS.some((domain) => WEBAPP_HOSTNAME.endsWith(domain));
export const EMBED_LIB_URL = process.env.NEXT_PUBLIC_EMBED_LIB_URL || `${WEBAPP_URL}/embed/embed.js`;
export const TRIAL_LIMIT_DAYS = 14;
export const MAX_SEATS_PER_TIME_SLOT = 1000;

/** Maximum duration allowed for an event in minutes (24 hours) */
export const MAX_EVENT_DURATION_MINUTES = 1440;

/** Minimum duration allowed for an event in minutes */
export const MIN_EVENT_DURATION_MINUTES = 1;

export const HOSTED_CAL_FEATURES = process.env.NEXT_PUBLIC_HOSTED_CAL_FEATURES || !IS_SELF_HOSTED;

export const PUBLIC_QUERY_RESERVATION_INTERVAL_SECONDS =
  parseInt(process.env.NEXT_PUBLIC_QUERY_RESERVATION_INTERVAL_SECONDS ?? "", 10) || 30;

// Must be lower than PUBLIC_QUERY_RESERVATION_INTERVAL_SECONDS
export const PUBLIC_QUERY_RESERVATION_STALE_TIME_SECONDS =
  parseInt(process.env.NEXT_PUBLIC_QUERY_RESERVATION_STALE_TIME_SECONDS ?? "", 10) || 20;
export const PUBLIC_QUERY_AVAILABLE_SLOTS_INTERVAL_SECONDS =
  parseInt(process.env.NEXT_PUBLIC_QUERY_AVAILABLE_SLOTS_INTERVAL_SECONDS ?? "", 10) || 5 * 60;
export const PUBLIC_INVALIDATE_AVAILABLE_SLOTS_ON_BOOKING_FORM =
  process.env.NEXT_PUBLIC_INVALIDATE_AVAILABLE_SLOTS_ON_BOOKING_FORM === "1";

// Enables a feature for x% of all visitors. Takes a number between 0 and 100.
export const PUBLIC_QUICK_AVAILABILITY_ROLLOUT =
  parseInt(process.env.NEXT_PUBLIC_QUICK_AVAILABILITY_ROLLOUT ?? "", 10) || 0;

/** @deprecated use `WEBAPP_URL` */
export const NEXT_PUBLIC_BASE_URL = process.env.NEXT_PUBLIC_WEBAPP_URL || `https://${process.env.VERCEL_URL}`;
export const LOGO = "/brand/kalienda-logo.svg";
// Variantes pour fond sombre (US-C3) : le logo est duotone (marine + turquoise) ; l'aplatir en
// blanc pur perdrait l'accent de marque. Ici le marine passe en blanc, le turquoise en 400.
export const LOGO_DARK = "/brand/kalienda-logo-dark.svg";
export const LOGO_ICON = "/brand/kalienda-mark.svg";
export const LOGO_ICON_DARK = "/brand/kalienda-mark-dark.svg";
export const AVATAR_FALLBACK = "/avatar.svg";
export const FAVICON_16 = "/favicon-16x16.png";
export const FAVICON_32 = "/favicon-32x32.png";
export const APPLE_TOUCH_ICON = "/apple-touch-icon.png";
export const MSTILE_ICON = "/mstile-150x150.png";
export const ANDROID_CHROME_ICON_192 = "/android-chrome-192x192.png";
export const ANDROID_CHROME_ICON_256 = "/android-chrome-256x256.png";
// Liens sortants vers le site public : dérivés de MARKETING_SITE_URL pour qu'un futur changement de
// marque ne touche qu'un seul endroit. Ils étaient bâtis sur WEBSITE_URL, qui vaut l'URL de
// l'application en production (voir MARKETING_SITE_URL) — ils pointaient donc l'utilisateur vers
// l'app, pas vers la vitrine.
// Seules ROADMAP et DOCS_URL sont consommées aujourd'hui (menu utilisateur, page 404, e-mail
// d'enregistrement) ; les autres sont conservées telles quelles, sans usage, pour ne pas créer de
// conflit au prochain merge upstream.
export const ROADMAP = `${MARKETING_SITE_URL}/roadmap/`;
export const DESKTOP_APP_LINK = MARKETING_SITE_URL;
export const JOIN_COMMUNITY = MARKETING_SITE_URL;
export const POWERED_BY_URL = MARKETING_SITE_URL;
/** Documentation utilisateur = le centre d'aide de la vitrine (`/aide/`), seule doc publiée. */
export const DOCS_URL = `${MARKETING_SITE_URL}/aide/`;
export const DEVELOPER_DOCS = MARKETING_SITE_URL;
export const SEO_IMG_DEFAULT = `${CAL_URL}/og-image.png`;
// Préfixe absolu des images OG dynamiques : on lui concatène le chemin rendu par
// `constructGenericImage` / `constructMeetingImage` / `constructAppImage` (voir OgImages.tsx).
//
// ⚠️ Upstream faisait passer ces images par l'API d'optimisation de Next
// (`/_next/image?w=1200&q=100&url=…`, « 80 % plus léger »). Ce détour est retiré : l'optimiseur est
// désactivé chez nous (`images: { unoptimized: true }`) et répond 404 en auto-hébergé, ce qui
// privait TOUTES les pages d'aperçu de partage — vérifié en production le 2026-08-08. La route OG
// rend déjà un PNG en 1200×630 ; on la sert donc telle quelle.
export const SEO_IMG_OGIMG = CAL_URL;
export const SEO_IMG_OGIMG_VIDEO = `${CAL_URL}/video-og-image.png`;
export const IS_STRIPE_ENABLED = !!(
  process.env.STRIPE_CLIENT_ID &&
  process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY &&
  process.env.STRIPE_PRIVATE_KEY
);
/** This has correct value only server side. When you want to use client side, go for IS_TEAM_BILLING_ENABLED_CLIENT. I think we should use the _CLIENT one only everywhere so that it works reliably everywhere on client as well as server  */
export const IS_TEAM_BILLING_ENABLED = !!(IS_STRIPE_ENABLED && HOSTED_CAL_FEATURES);

export const IS_TEAM_BILLING_ENABLED_CLIENT =
  !!process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY && HOSTED_CAL_FEATURES;

export const FULL_NAME_LENGTH_MAX_LIMIT = 50;
export const API_NAME_LENGTH_MAX_LIMIT = 80;
export const MINUTES_TO_BOOK = process.env.NEXT_PUBLIC_MINUTES_TO_BOOK || "5";
export const ENABLE_PROFILE_SWITCHER = process.env.NEXT_PUBLIC_ENABLE_PROFILE_SWITCHER === "1";
// Needed for orgs
export const ALLOWED_HOSTNAMES = JSON.parse(`[${process.env.ALLOWED_HOSTNAMES || ""}]`) as string[];
export const RESERVED_SUBDOMAINS = JSON.parse(`[${process.env.RESERVED_SUBDOMAINS || ""}]`) as string[];

export const ORGANIZATION_SELF_SERVE_PRICE = parseFloat(
  process.env.NEXT_PUBLIC_ORGANIZATIONS_SELF_SERVE_PRICE_NEW || "37"
);

// Needed for emails in E2E
export const IS_MAILHOG_ENABLED = process.env.E2E_TEST_MAILHOG_ENABLED === "1";
export const CALCOM_VERSION = process.env.NEXT_PUBLIC_CALCOM_VERSION as string;

/**
 * Version **produit** affichée à l'utilisateur (pied de la barre latérale), distincte de
 * `CALCOM_VERSION` qui trace la base upstream (`apps/web/package.json`) et sert de cache-buster.
 *
 * ⚠️ Source de vérité : `releases[0].version` du journal des versions publié sur
 * {@link MARKETING_SITE_URL}/changelog/ (dépôt `rdv-pro-website`,
 * `src/i18n/content/changelog.ts`). Les deux doivent rester alignés : un praticien qui lit
 * « v0.9.1 » dans l'app doit retrouver ce même numéro en haut du changelog. À bumper ici à chaque
 * nouvelle entrée publiée là-bas.
 */
export const APP_VERSION = "0.9.1";

export const APP_CREDENTIAL_SHARING_ENABLED =
  !!process.env.CALCOM_CREDENTIAL_SYNC_SECRET && !!process.env.CALCOM_APP_CREDENTIAL_ENCRYPTION_KEY;
export const CREDENTIAL_SYNC_SECRET = process.env.CALCOM_CREDENTIAL_SYNC_SECRET;
export const CREDENTIAL_SYNC_SECRET_HEADER_NAME =
  process.env.CALCOM_CREDENTIAL_SYNC_HEADER_NAME || "calcom-credential-sync-secret";

export const CREDENTIAL_SYNC_ENDPOINT = process.env.CALCOM_CREDENTIAL_SYNC_ENDPOINT;

// Service Account Encryption Key for encrypting/decrypting service account keys
export const SERVICE_ACCOUNT_ENCRYPTION_KEY = process.env.CALCOM_SERVICE_ACCOUNT_ENCRYPTION_KEY;

/**
 * Palette de marque Kalienda — SOURCE DE VÉRITÉ côté JS/TS.
 *
 * Système bi-ton : le TURQUOISE porte l'action (boutons, accents), le MARINE porte la
 * structure (surfaces sombres, logo). Miroir exact de :
 *   - `packages/config/theme/tokens.css`  → `--kalienda-turquoise-*` / `--kalienda-marine-*`
 *   - `rdv-pro-website/src/styles/global.css` → `--color-brand-*` / `--color-navy-*`
 *
 * ⚠️ Il n'y a PAS de génération automatique : toute modification d'une valeur doit être
 * répercutée à la main dans ces deux fichiers. Voir `docs/branding/tokens.md`.
 * Principe : poser Kalienda comme *défaut* des tokens, jamais un hex codé en dur dans un composant.
 */
export const KALIENDA_TURQUOISE = {
  50: "#ecfdfb",
  100: "#cbf9f4",
  200: "#99f2eb",
  300: "#5de6dd",
  400: "#24d1c8",
  500: "#10b8b0",
  600: "#0b938d",
  700: "#0c7671",
  800: "#0f5d5a",
  900: "#124d4b",
  950: "#032c2b",
} as const;

export const KALIENDA_MARINE = {
  50: "#eff4fb",
  100: "#dbe6f4",
  200: "#bccfe9",
  300: "#90add8",
  400: "#5c83c1",
  500: "#3862a6",
  600: "#2a4d86",
  700: "#223f6c",
  800: "#132f5f",
  900: "#05245a",
  950: "#03163a",
} as const;

// Couleur de marque par défaut (surchargée par `User.brandColor`/`darkBrandColor` au runtime).
// Light = turquoise-600 : c'est le ton d'action/sélection de la maquette du site (Hero.astro —
// jour sélectionné `bg-brand-600 text-white`) ; sa luminance < 0,5 fait que getWCAGContrastColor
// rend automatiquement du texte BLANC (décision design 2026-07-24, cf. docs/branding/PLAN-COULEURS.md §0/§5).
// Dark = turquoise-400 (plus lumineux pour rester lisible sur fond sombre ; texte noir auto).
// Doivent rester des hex littéraux : `getBrandColours` fait du calcul de couleur dessus.
export const DEFAULT_LIGHT_BRAND_COLOR = KALIENDA_TURQUOISE[600];
export const DEFAULT_DARK_BRAND_COLOR = KALIENDA_TURQUOISE[400];

/**
 * Surfaces par défaut des pages publiques — **miroir des fallbacks de `tokens.css`**.
 *
 * Pourquoi ces valeurs vivent ici : les écrans de personnalisation (« Couleurs de rendez-vous »)
 * doivent afficher dans leurs sélecteurs la couleur *réellement* appliquée quand le praticien n'a
 * rien personnalisé. Ces valeurs étaient auparavant recopiées à la main dans le composant, et
 * avaient déjà **dérivé** des vrais tokens (ex. la pastille de durée est passée au turquoise au
 * Lot 1 alors que le menu affichait encore un gris) : le menu mentait.
 *
 * ⚠️ Ce sont des hex **littéraux** : un `<input type="color">` ne sait pas résoudre `var(--…)`.
 * Toute modification d'un fallback dans `packages/config/theme/tokens.css` doit donc être
 * répercutée ici. Voir `docs/branding/tokens.md` (§ miroirs).
 */
export const KALIENDA_SURFACES = {
  /** Fond du widget de réservation — `--cal-booking-bg-fallback` (bg / bg-muted). */
  bookingBg: { light: "#ffffff", dark: "#171717" },
  /** Fond des boutons de créneau au repos — `--color-booking-slots-bg` (bg). */
  slotBg: { light: "#ffffff", dark: "#0f0f0f" },
  /** Fond de la carte de confirmation — `--cal-booking-success-bg-fallback` (bg / bg-muted). */
  successBg: { light: "#ffffff", dark: "#171717" },
  /** Profil public : bandeau du nom — `--color-profile-name-bg` (bg). */
  profileNameBg: { light: "#ffffff", dark: "#0f0f0f" },
  /** Profil public : carte d'acte — `--cal-profile-card-bg-fallback` (bg / bg-muted). */
  profileCardBg: { light: "#ffffff", dark: "#171717" },
  /** Profil public : survol de carte — `--cal-profile-card-hover-fallback` (bg-muted / bg-subtle). */
  profileCardHover: { light: "#f8fafc", dark: "#262626" },
  /** Profil public : pastille de durée — `--cal-profile-duration-bg-fallback` (turquoise 50 / 950). */
  profileDurationBg: { light: "#ecfdfb", dark: "#032c2b" },
  /** Profil public : pilule de filtre ACTIVE — `--cal-profile-pill-active` (marine 900 / 50). */
  profilePillActive: { light: "#05245a", dark: "#eff4fb" },
  /** Fond de page des vitrines publiques — `--cal-public-page-bg` (slate-100 / marine-950). */
  publicPageBg: { light: "#f1f5f9", dark: "#03163a" },
} satisfies Record<string, { light: string; dark: string }>;

export const TOP_BANNER_HEIGHT = 40;

export const IS_PREMIUM_USERNAME_ENABLED =
  (IS_CALCOM || (process.env.NEXT_PUBLIC_IS_E2E && IS_STRIPE_ENABLED)) &&
  process.env.NEXT_PUBLIC_STRIPE_PREMIUM_PLAN_PRICE_MONTHLY;

// Max number of invites to join a team/org that can be sent at once
export const MAX_NB_INVITES = 100;

export const URL_PROTOCOL_REGEX = /(^\w+:|^)\/\//;

export const IS_VISUAL_REGRESSION_TESTING = Boolean(globalThis.window?.Meticulous?.isRunningAsTest);

export const BOOKER_NUMBER_OF_DAYS_TO_LOAD = parseInt(
  process.env.NEXT_PUBLIC_BOOKER_NUMBER_OF_DAYS_TO_LOAD ?? "0",
  10
);

export const CLOUDFLARE_SITE_ID = process.env.NEXT_PUBLIC_CLOUDFLARE_SITEKEY;
export const CLOUDFLARE_USE_TURNSTILE_IN_BOOKER = process.env.NEXT_PUBLIC_CLOUDFLARE_USE_TURNSTILE_IN_BOOKER;
export const ORG_SELF_SERVE_ENABLED = process.env.NEXT_PUBLIC_ORG_SELF_SERVE_ENABLED === "1";
export const ORG_MINIMUM_PUBLISHED_TEAMS_SELF_SERVE = 0;
export const ORG_MINIMUM_PUBLISHED_TEAMS_SELF_SERVE_HELPER_DIALOGUE = 1;

export const CALCOM_PRIVATE_API_ROUTE = process.env.CALCOM_PRIVATE_API_ROUTE || "https://goblin.cal.com";
// Liens légaux → pages RÉELLES du site vitrine Kalienda.
// ⚠️ Les défauts hérités d'upstream (`/privacy` et `/terms`) renvoyaient **404** sur kalienda.be.
// Ces constantes servent à la fois à l'inscription praticien (signup-view) ET au formulaire de
// réservation **patient** (BookEventForm) — donc des liens morts côté patient.
// ⚠️ Bâties sur MARKETING_SITE_URL, jamais sur WEBSITE_URL : cette dernière vaut l'URL de
// l'application en production (voir MARKETING_SITE_URL), et `https://rdv.kalienda.be/cgu` renvoyait
// bel et bien un 404 — vérifié le 2026-08-08, avant correction.
// Pages publiées : /cgu/ et /confidentialite/ (politique complète, publiée avec la v0.9.1 — elle
// remplace l'ancre #confidentialite de /mentions-legales/, qui n'était qu'une section). Le DPA vit
// sur /dpa/ (non référencé ici).
export const WEBSITE_PRIVACY_POLICY_URL =
  process.env.NEXT_PUBLIC_WEBSITE_PRIVACY_POLICY_URL || `${MARKETING_SITE_URL}/confidentialite/`;
export const WEBSITE_TERMS_URL = process.env.NEXT_PUBLIC_WEBSITE_TERMS_URL || `${MARKETING_SITE_URL}/cgu/`;
export const LINGO_DOT_DEV_API_KEY = process.env.LINGO_DOT_DEV_API_KEY;

/**
 * The maximum number of days we should check for if we don't find all required bookable days
 * Counter start from current day and we would like to not go beyond 2 months(max days possible) from current day.
 */
export const ROLLING_WINDOW_PERIOD_MAX_DAYS_TO_CHECK = 30 + 31;

export const TRANSCRIPTION_STARTED_ICON = IS_PRODUCTION
  ? `${WEBAPP_URL}/sparkles-red.svg`
  : `https://app.cal.com/sparkles-red.svg`;

export const TRANSCRIPTION_STOPPED_ICON = IS_PRODUCTION
  ? `${WEBAPP_URL}/sparkles.svg`
  : `https://app.cal.com/sparkles.svg`;

export const RECORDING_DEFAULT_ICON = IS_PRODUCTION
  ? `${WEBAPP_URL}/start-recording.svg`
  : `https://app.cal.com/start-recording.svg`;

export const RECORDING_IN_PROGRESS_ICON = IS_PRODUCTION
  ? `${WEBAPP_URL}/stop-recording.svg`
  : `https://app.cal.com/stop-recording.svg`;

export const SCOPE_USERINFO_PROFILE = "https://www.googleapis.com/auth/userinfo.profile";
export const SCOPE_USERINFO_EMAIL = "https://www.googleapis.com/auth/userinfo.email";
export const GOOGLE_OAUTH_SCOPES = [SCOPE_USERINFO_PROFILE, SCOPE_USERINFO_EMAIL];
export const GOOGLE_CALENDAR_SCOPES = [
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/calendar.readonly",
];
export const DIRECTORY_IDS_TO_LOG = process.env.DIRECTORY_IDS_TO_LOG?.split(",") || [];
export const SCANNING_WORKFLOW_STEPS = !!(!IS_SELF_HOSTED && process.env.IFFY_API_KEY);

// Cloudflare URL Scanner - checks URLs for malicious content in workflows and event types
export const URL_SCANNING_ENABLED =
  !!process.env.CLOUDFLARE_URL_SCANNER_API_TOKEN && !!process.env.CLOUDFLARE_ACCOUNT_ID;

export const DUB_SMS_DOMAIN = process.env.DUB_SMS_DOMAIN;
export const DUB_SMS_FOLDER_ID = process.env.DUB_SMS_FOLDER_ID;

export const CAL_VIDEO_MEETING_LINK_FOR_TESTING = process.env.CAL_VIDEO_MEETING_LINK_FOR_TESTING;

export const IS_SMS_CREDITS_ENABLED =
  !!process.env.NEXT_PUBLIC_STRIPE_CREDITS_PRICE_ID || !!process.env.NEXT_PUBLIC_IS_E2E;
export const DATABASE_CHUNK_SIZE = parseInt(process.env.DATABASE_CHUNK_SIZE || "25", 10);

export const NEXTJS_CACHE_TTL = 3600; // 1 hour

export const DEFAULT_GROUP_ID = "default_group_id";

// Environment variable for configuring past booking reschedule behavior per team. A comma separated list of team IDs(e.g. '1,2,3')
/* This is an internal environment variable and is not meant to be used by the self-hosters. It is planned to be removed later by either having it as an option in Event Type or by some other customer configurable approaches*/
export const ENV_PAST_BOOKING_RESCHEDULE_CHANGE_TEAM_IDS =
  process.env._CAL_INTERNAL_PAST_BOOKING_RESCHEDULE_CHANGE_TEAM_IDS;

// Cal Video (Daily) app identifiers
export const CAL_VIDEO = "daily-video";
export const CAL_VIDEO_TYPE = "daily_video";

export const ORG_TRIAL_DAYS = process.env.STRIPE_ORG_TRIAL_DAYS
  ? Math.max(0, parseInt(process.env.STRIPE_ORG_TRIAL_DAYS, 10))
  : null;

export const IS_API_V2_E2E = process.env.IS_E2E === "true";

export const ENABLE_ASYNC_TASKER =
  process.env.ENABLE_ASYNC_TASKER === "true" && !process.env.NEXT_PUBLIC_IS_E2E && !IS_API_V2_E2E;

export const MICROSOFT_CALENDAR_SCOPES = [
  "User.Read",
  "Calendars.Read",
  "Calendars.ReadWrite",
  "offline_access",
];
